import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FaPlus, FaEdit, FaTrash, FaEye, FaTimes, FaSave, FaUpload, FaBox } from 'react-icons/fa';
import LedgerModal from '../../components/common/LedgerModal';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import './Inventory.css';

const Inventory = () => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [manufacturingCategories, setManufacturingCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);

  const [formData, setFormData] = useState({
    company: '',
    itemName: '',
    itemNo: '',
    itemQuantity: '',
    unit: 'pcs',
    vendor: '',
    mrp: '',
    sellingPrice: '',
    bulkPrice: '',
    discount: 0,
    productDescription: '',
    category: '',
    images: []
  });

  // Extract unique categories from existing inventory items and manufacturing categories
  const getAvailableCategories = () => {
    const categorySet = new Set();

    // Add categories from existing inventory
    inventoryItems.forEach(item => {
      if (item.category && item.category.trim()) {
        categorySet.add(item.category.trim());
      }
    });

    // Add categories from manufacturing categories
    manufacturingCategories.forEach(cat => {
      if (cat.name && cat.name.trim()) {
        categorySet.add(cat.name.trim());
      }
    });

    return Array.from(categorySet).sort();
  };

  const availableCategories = getAvailableCategories();

  useEffect(() => {
    loadInventoryItems();
    loadCompanies();
    loadVendors();
    loadManufacturingCategories();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    loadInventoryItems();
  }, [search, companyFilter, categoryFilter]);

  const loadInventoryItems = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (companyFilter) params.append('company', companyFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      if (search) params.append('search', search);

      const response = await api.get(`/api/inventory?${params.toString()}`);
      setInventoryItems(response.data.inventoryItems || []);
    } catch (err) {
      console.error('Error loading inventory items:', err);
      setError('Failed to load inventory items');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await api.get('/api/companies');
      setCompanies(response.data.companies || []);
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  };

  const loadVendors = async () => {
    try {
      const response = await api.get('/api/vendors');
      setVendors(response.data.vendors || []);
    } catch (err) {
      console.error('Error loading vendors:', err);
    }
  };

  const loadManufacturingCategories = async () => {
    try {
      const response = await api.get('/api/manufacturing-categories');
      setManufacturingCategories(response.data.manufacturingCategories || []);
    } catch (err) {
      console.error('Error loading manufacturing categories:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this inventory item?')) {
      try {
        await api.delete(`/api/inventory/${id}`);
        setInventoryItems(inventoryItems.filter(item => item._id !== id));
      } catch (err) {
        console.error('Error deleting inventory item:', err);
        setError('Failed to delete inventory item');
      }
    }
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData({
      company: '',
      itemName: '',
      itemNo: '',
      itemQuantity: '',
      unit: 'pcs',
      vendor: '',
      mrp: '',
      sellingPrice: '',
      bulkPrice: '',
      discount: 0,
      productDescription: '',
      category: '',
      images: []
    });
    setCategorySearchQuery('');
    setShowCategoryDropdown(false);
    setVendorSearchQuery('');
    setShowVendorDropdown(false);
    setShowModal(true);
    setError('');
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    const categoryValue = item.category || '';
    const vendorValue = item.vendor || '';
    setFormData({
      company: item.company?._id || '',
      itemName: item.itemName || '',
      itemNo: item.itemNo || '',
      itemQuantity: item.itemQuantity || '',
      unit: item.unit || 'pcs',
      vendor: vendorValue,
      mrp: item.mrp || '',
      sellingPrice: item.sellingPrice || '',
      bulkPrice: item.bulkPrice || '',
      discount: item.discount || 0,
      productDescription: item.productDescription || '',
      category: categoryValue,
      images: item.images || []
    });
    setCategorySearchQuery(categoryValue);
    setShowCategoryDropdown(false);
    setVendorSearchQuery(vendorValue);
    setShowVendorDropdown(false);
    setShowModal(true);
    setError('');
  };

  const openViewModal = (item) => {
    setViewingItem(item);
  };

  const openLedgerForItem = () => {
    setShowLedgerModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setViewingItem(null);
    setCategorySearchQuery('');
    setShowCategoryDropdown(false);
    setVendorSearchQuery('');
    setShowVendorDropdown(false);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle category field specially for autocomplete
    if (name === 'category') {
      setCategorySearchQuery(value);
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      // Show dropdown if there are matching categories
      if (value.length > 0) {
        const filtered = availableCategories.filter(cat =>
          cat.toLowerCase().includes(value.toLowerCase())
        );
        setShowCategoryDropdown(filtered.length > 0);
      } else {
        setShowCategoryDropdown(false);
      }
    } else if (name === 'vendor') {
      // Handle vendor field for autocomplete
      setVendorSearchQuery(value);
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      // Show dropdown if there are matching vendors
      if (value.length > 0) {
        const filtered = vendors.filter(vendor =>
          vendor.name.toLowerCase().includes(value.toLowerCase())
        );
        setShowVendorDropdown(filtered.length > 0);
      } else {
        setShowVendorDropdown(false);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCategorySelect = (category) => {
    setFormData(prev => ({
      ...prev,
      category: category
    }));
    setCategorySearchQuery(category);
    setShowCategoryDropdown(false);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const imagePromises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(imagePromises).then(images => {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...images]
      }));
    });
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const calculateDiscountedPrice = () => {
    const sellingPrice = parseFloat(formData.sellingPrice) || 0;
    const discount = parseFloat(formData.discount) || 0;
    return sellingPrice - (sellingPrice * discount / 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Use vendorSearchQuery for vendor field since that's what the user sees
      const vendorValue = vendorSearchQuery.trim();
      if (!formData.itemQuantity || !formData.unit || !vendorValue || !formData.sellingPrice) {
        throw new Error('Please fill in all required fields');
      }

      if (parseFloat(formData.sellingPrice) <= 0) {
        throw new Error('Selling Price must be greater than 0');
      }
      if (formData.mrp && parseFloat(formData.mrp) <= 0) {
        throw new Error('MRP must be greater than 0 if provided');
      }

      if (parseInt(formData.itemQuantity) < 0) {
        throw new Error('Item quantity cannot be negative');
      }

      const itemData = {
        ...formData,
        vendor: vendorValue,
        itemQuantity: parseInt(formData.itemQuantity),
        mrp: parseFloat(formData.mrp),
        sellingPrice: parseFloat(formData.sellingPrice),
        bulkPrice: formData.bulkPrice ? parseFloat(formData.bulkPrice) : undefined,
        discount: parseFloat(formData.discount)
      };

      if (editingItem) {
        const response = await api.put(`/api/inventory/${editingItem._id}`, itemData);
        setInventoryItems(inventoryItems.map(item => item._id === editingItem._id ? response.data.inventoryItem : item));
      } else {
        await api.post('/api/inventory', itemData);
        // Reload inventory items from server to ensure new item appears correctly
        await loadInventoryItems();
      }

      closeModal();
    } catch (err) {
      // Handle validation errors with more detail
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        setError(`Validation error: ${err.response.data.errors.join(', ')}`);
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to save inventory item');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = inventoryItems.filter(item => {
    const matchesSearch = item.itemName.toLowerCase().includes(search.toLowerCase()) ||
      (item.productDescription && item.productDescription.toLowerCase().includes(search.toLowerCase())) ||
      (item.itemNo && item.itemNo.toLowerCase().includes(search.toLowerCase())) ||
      (item.category && item.category.toLowerCase().includes(search.toLowerCase()));

    const matchesCompany = !companyFilter || item.company?._id === companyFilter;
    const matchesCategory = !categoryFilter || item.category === categoryFilter;

    return matchesSearch && matchesCompany && matchesCategory;
  });

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
  const pageStart = (currentPage - 1) * itemsPerPage;
  const pageEnd = pageStart + itemsPerPage;
  const paginatedItems = filteredItems.slice(pageStart, pageEnd);

  if (loading && inventoryItems.length === 0) return <Loading />;

  return (
    <div className="inventory-container">
      <div className="inventory-header">
        <div>
          <h1 className="inventory-title">Inventory Management</h1>
          <p className="inventory-subtitle">Create, edit, and manage inventory items</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="add-item-btn" onClick={openAddModal}>
            <FaPlus /> Add New Item
          </button>
        </div>
      </div>

      <div className="filters-section">
        <input
          type="text"
          placeholder="Search items by name, code, or description..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-input"
        />

        <div className="filter-row">
          <select
            value={companyFilter}
            onChange={e => setCompanyFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Companies</option>
            {companies.map(company => (
              <option key={company._id} value={company._id}>
                {company.name}
              </option>
            ))}
          </select>

          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Categories</option>
            {availableCategories.map(category => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          <button onClick={loadInventoryItems} className="refresh-btn">
            Refresh
          </button>
        </div>
      </div>

      <div className="inventory-table-wrapper">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
            Showing {filteredItems.length === 0 ? 0 : (pageStart + 1)}-
            {Math.min(filteredItems.length, pageEnd)} of {filteredItems.length}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
            <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>Page {currentPage} of {totalPages}</span>
            <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>Next</button>
          </div>
        </div>
        <table className="inventory-table">
          <thead>
            <tr>
              <th>Item Name</th>
              <th>Company</th>
              <th>Category</th>
              <th>MRP</th>
              <th>Selling Price</th>
              <th>Discounted Price</th>
              <th>Quantity</th>
              <th>Unit</th>
              <th>Vendor</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={11} style={{ textAlign: 'center' }}>No inventory items found.</td>
              </tr>
            ) : (
              paginatedItems.map(item => (
                <tr key={item._id}>
                  <td>
                    <div className="item-name-cell">
                      <span className="item-name">{item.itemName}</span>
                      {item.images && item.images.length > 0 && (
                        <img src={item.images[0]} alt={item.itemName} className="item-thumbnail" />
                      )}
                    </div>
                  </td>
                  <td>{item.company?.name || 'N/A'}</td>
                  <td>{item.category || 'N/A'}</td>
                  <td>{item.mrp ? `₹${item.mrp.toFixed(2)}` : 'N/A'}</td>
                  <td>₹{item.sellingPrice?.toFixed(2)}</td>
                  <td>₹{item.discountedPrice?.toFixed(2)}</td>
                  <td>
                    <span className={`quantity-badge ${item.itemQuantity > 20 ? 'high' : item.itemQuantity > 10 ? 'medium' : 'low'}`}>
                      {item.itemQuantity}
                    </span>
                  </td>
                  <td>{item.unit || 'N/A'}</td>
                  <td>{item.vendor || 'N/A'}</td>
                  <td>
                    <span className={`status-badge ${item.status}`}>
                      {item.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons-row">
                      <button
                        onClick={() => openViewModal(item)}
                        className="action-btn view"
                        title="View Item"
                      >
                        <FaEye /> View
                      </button>
                      <button
                        onClick={() => openEditModal(item)}
                        className="action-btn edit"
                        title="Edit Item"
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="action-btn delete"
                        title="Delete Item"
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingItem ? 'Edit Inventory Item' : 'Add New Inventory Item'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="inventory-form">
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="company">Company *</label>
                    <select
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                    >
                      <option value="">Select a company</option>
                      {companies.map(company => (
                        <option key={company._id} value={company._id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="itemNo">Item No.</label>
                    <input
                      type="text"
                      id="itemNo"
                      name="itemNo"
                      value={formData.itemNo}
                      onChange={handleInputChange}
                      placeholder="Optional item number"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="itemName">Item Name</label>
                  <input
                    type="text"
                    id="itemName"
                    name="itemName"
                    value={formData.itemName}
                    onChange={handleInputChange}
                    placeholder="Enter item name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="productDescription">Product Description</label>
                  <textarea
                    id="productDescription"
                    name="productDescription"
                    value={formData.productDescription}
                    onChange={handleInputChange}
                    placeholder="Enter product description"
                    rows="3"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label htmlFor="category">Category</label>
                    <input
                      type="text"
                      id="category"
                      name="category"
                      value={categorySearchQuery}
                      onChange={handleInputChange}
                      onFocus={() => {
                        if (categorySearchQuery.length > 0) {
                          const filtered = availableCategories.filter(cat =>
                            cat.toLowerCase().includes(categorySearchQuery.toLowerCase())
                          );
                          setShowCategoryDropdown(filtered.length > 0);
                        }
                      }}
                      onBlur={() => {
                        // Delay to allow click on dropdown item
                        setTimeout(() => setShowCategoryDropdown(false), 200);
                      }}
                      placeholder="Type category name..."
                      style={{ width: '100%' }}
                    />
                    {showCategoryDropdown && availableCategories.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        marginTop: '2px'
                      }}>
                        {availableCategories
                          .filter(cat =>
                            cat.toLowerCase().includes(categorySearchQuery.toLowerCase())
                          )
                          .map(category => (
                            <div
                              key={category}
                              onClick={() => handleCategorySelect(category)}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                            >
                              {category}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="itemQuantity">Item Quantity *</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                      <input
                        type="number"
                        id="itemQuantity"
                        name="itemQuantity"
                        value={formData.itemQuantity}
                        onChange={handleInputChange}
                        placeholder="0"
                        min="0"
                        required
                        style={{ flex: 1 }}
                      />
                      <div style={{ position: 'relative', width: '120px' }}>
                        <input
                          type="text"
                          id="unit"
                          name="unit"
                          value={formData.unit}
                          onChange={handleInputChange}
                          placeholder="Unit"
                          required
                          style={{ width: '100%' }}
                          list="unit-options"
                        />
                        <datalist id="unit-options">
                          <option value="pcs" />
                          <option value="piece" />
                          <option value="pieces" />
                          <option value="dozen" />
                          <option value="carton" />
                          <option value="kg" />
                          <option value="gram" />
                          <option value="g" />
                          <option value="box" />
                          <option value="packet" />
                          <option value="meter" />
                          <option value="liter" />
                          <option value="bundle" />
                          <option value="set" />
                        </datalist>
                      </div>
                    </div>
                  </div>

                  <div className="form-group" style={{ position: 'relative' }}>
                    <label htmlFor="vendor">Vendor *</label>
                    <input
                      type="text"
                      id="vendor"
                      name="vendor"
                      value={vendorSearchQuery}
                      onChange={handleInputChange}
                      onFocus={() => {
                        if (vendorSearchQuery.length > 0) {
                          const filtered = vendors.filter(vendor =>
                            vendor.name.toLowerCase().includes(vendorSearchQuery.toLowerCase())
                          );
                          setShowVendorDropdown(filtered.length > 0);
                        }
                      }}
                      onBlur={() => {
                        // Delay to allow click on dropdown item
                        setTimeout(() => setShowVendorDropdown(false), 200);
                      }}
                      placeholder="Type vendor name..."
                      required
                      style={{ width: '100%' }}
                    />
                    {showVendorDropdown && vendors.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        marginTop: '4px'
                      }}>
                        {vendors
                          .filter(vendor =>
                            vendor.name.toLowerCase().includes(vendorSearchQuery.toLowerCase())
                          )
                          .map(vendor => (
                            <div
                              key={vendor._id}
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  vendor: vendor.name
                                }));
                                setVendorSearchQuery(vendor.name);
                                setShowVendorDropdown(false);
                              }}
                              style={{
                                padding: '10px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f5f5f5';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'white';
                              }}
                            >
                              {vendor.name}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Pricing Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="mrp">MRP</label>
                    <input
                      type="number"
                      id="mrp"
                      name="mrp"
                      value={formData.mrp}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="sellingPrice">Selling Price *</label>
                    <input
                      type="number"
                      id="sellingPrice"
                      name="sellingPrice"
                      value={formData.sellingPrice}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="bulkPrice">Bulk Price</label>
                    <input
                      type="number"
                      id="bulkPrice"
                      name="bulkPrice"
                      value={formData.bulkPrice}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="discount">Discount (%)</label>
                    <input
                      type="number"
                      id="discount"
                      name="discount"
                      value={formData.discount}
                      onChange={handleInputChange}
                      placeholder="0"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Discounted Price (Auto-calculated)</label>
                  <input
                    type="text"
                    value={`₹${calculateDiscountedPrice().toFixed(2)}`}
                    disabled
                    className="disabled-input"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Product Images</h3>
                <div className="form-group">
                  <label htmlFor="images">Upload Images</label>
                  <div className="image-upload-container">
                    <input
                      type="file"
                      id="images"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="image-upload-input"
                    />
                    <label htmlFor="images" className="image-upload-label">
                      <FaUpload /> Choose Images
                    </label>
                  </div>

                  {formData.images.length > 0 && (
                    <div className="image-preview-container">
                      {formData.images.map((image, index) => (
                        <div key={index} className="image-preview-item">
                          <img src={image} alt={`Preview ${index + 1}`} />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="remove-image-btn"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={loading}
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  <FaSave /> {loading ? 'Saving...' : (editingItem ? 'Update Item' : 'Add Item')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingItem && (
        <div className="modal-overlay" onClick={() => setViewingItem(null)}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Inventory Item Details</h2>
              <button className="modal-close" onClick={() => setViewingItem(null)}>
                <FaTimes />
              </button>
            </div>

            <div className="item-view-content">
              <div className="item-images-section">
                {viewingItem.images && viewingItem.images.length > 0 ? (
                  <div className="item-images-grid">
                    {viewingItem.images.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`${viewingItem.itemName} ${index + 1}`}
                        className="item-view-image"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="no-images-placeholder">No images available</div>
                )}
              </div>

              <div className="item-details-section">
                <div className="detail-group">
                  <h3>Basic Information</h3>
                  <div className="detail-item">
                    <label>Item No.:</label>
                    <span>{viewingItem.itemNo || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Item Name:</label>
                    <span>{viewingItem.itemName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Company:</label>
                    <span>{viewingItem.company?.name || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Category:</label>
                    <span>{viewingItem.category || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Unit:</label>
                    <span>{viewingItem.unit || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Vendor:</label>
                    <span>{viewingItem.vendor || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Description:</label>
                    <span>{viewingItem.productDescription || 'No description available'}</span>
                  </div>
                </div>

                <div className="detail-group">
                  <h3>Pricing Information</h3>
                  <div className="detail-item">
                    <label>MRP:</label>
                    <span>{viewingItem.mrp ? `₹${viewingItem.mrp.toFixed(2)}` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Selling Price:</label>
                    <span>₹{viewingItem.sellingPrice?.toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Bulk Price:</label>
                    <span>{viewingItem.bulkPrice ? `₹${viewingItem.bulkPrice.toFixed(2)}` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Discount:</label>
                    <span>{viewingItem.discount}%</span>
                  </div>
                  <div className="detail-item">
                    <label>Discounted Price:</label>
                    <span>₹{viewingItem.discountedPrice?.toFixed(2)}</span>
                  </div>
                </div>

                <div className="detail-group">
                  <h3>Inventory Information</h3>
                  <div className="detail-item">
                    <label>Quantity:</label>
                    <span className={`quantity-badge ${viewingItem.itemQuantity > 20 ? 'high' : viewingItem.itemQuantity > 10 ? 'medium' : 'low'}`}>
                      {viewingItem.itemQuantity}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className={`status-badge ${viewingItem.status}`}>
                      {viewingItem.status}
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 12 }}>
                <button className="btn-primary" onClick={openLedgerForItem}>
                  Add Ledger Entry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <LedgerModal
        isOpen={showLedgerModal}
        onClose={() => setShowLedgerModal(false)}
        defaultDescription={viewingItem ? `Inventory ${viewingItem.itemCode} - ${viewingItem.itemName}` : ''}
      />
    </div>
  );
};

export default Inventory;
